import { all } from 'deepmerge';
import { applyFormStateDefaults } from './forms.reducer';
import { provideMockStore } from '@ngrx/store/testing';
import * as Handlebars from 'handlebars/dist/handlebars';
import {
  ValidatorResult,
  ValidatorError
} from './decorators/validator';
import { DxFormValidatorDefinition } from './decorators/interfaces';
import {
  ValidationErrors,
  KeyValue
} from 'ngrx-forms';
import { DxFormState } from './form-state';

export function getValueByObjectPath<T = any>(
  obj: any,
  path: string
): T | null {
  for (const propertyKey of path.split('.')) {
    if (!obj.hasOwnProperty(propertyKey)) {
      return null;
    }
    obj = obj[propertyKey];
  }

  return obj;
}

export function deepmergeArrayGroups<T>(arrayInits: any, formState: T): T {
  for (const [key, arrayInit] of Object.entries(arrayInits)) {
    if (formState.hasOwnProperty(key)) {
      if (Array.isArray(formState[key])) {
        formState[key] = formState[key].map((item: any) =>
          all([arrayInit, item])
        );
      } else {
        formState[key] = deepmergeArrayGroups(arrayInit, formState[key]);
      }
    }
  }
  return formState;
}

export function buildNewFormArray<T extends { order: number }>(
  formArrayValue: T[],
  newItem: Partial<T>,
  start: boolean
): T[] {
  let order = 0;

  if (formArrayValue.length) {
    if (start) {
      order =
        [...formArrayValue].sort((a, b) => b.order - a.order)[0].order + 1;
    } else {
      order =
        [...formArrayValue].sort((a, b) => b.order - a.order)[
          formArrayValue.length - 1
        ].order - 1;
    }
  }

  return [...formArrayValue, { ...(newItem as object), order } as T];
}

export function getFormIdFromControlId(controlId: string): string {
  return (controlId && controlId.match(/^([^\.]+)/)[0]) || null;
}

export function splitControlIdInParentAndControlId(
  controlId: string
): string[] | null {
  if (controlId) {
    const match = controlId.match(/(.+)\.([^\.]+)$/);
    if (match) {
      return match.slice(1, 3);
    } else {
      return [controlId];
    }
  }
  return null;
}

export function splitControlIdInRootAndPath(
  controlId: string
): string[] | null {
  if (controlId) {
    const match = controlId.match(/^([^\.]+)\.(.+)/);
    if (match) {
      return match.slice(1, 3);
    } else {
      return [controlId];
    }
  }
  return null;
}

export function getStoreStateWithForm<Form, RawForm>(
  formId: string,
  formRegister: Partial<DxFormState<Form, RawForm>>,
  initial: Partial<Form> = {}
) {
  return {
    dx: {
      forms: {
        [formId]: applyFormStateDefaults(formId, {
          ...formRegister,
          initial: { ...formRegister.initial, ...initial }
        })
      }
    }
  };
}

export function provideMockStoreWithForm<Form, RawForm>(
  formId: string,
  formRegister: Partial<DxFormState<Form, RawForm>>,
  initial: Partial<Form> = {}
) {
  return provideMockStore({
    initialState: getStoreStateWithForm(formId, formRegister, initial)
  });
}

export function validationResultRoValidationError(
  result: ValidatorResult,
  validatorErrorTemplate: string,
  data: KeyValue
): ValidatorError | null {
  if (
    result !== null &&
    !(result && typeof result === 'object' && Object.keys(result).length === 0)
  ) {
    let error = result;
    if (validatorErrorTemplate) {
      error = Handlebars.compile(validatorErrorTemplate)({
        ...data,
        error: result
      });
    }
    return error;
  }
  return null;
}

export function controlValidatorWrapper<Value>(
  validator: (value: Value) => ValidatorResult,
  definition: DxFormValidatorDefinition<any, any>
): (value: Value) => ValidationErrors | null {
  return function(value: Value) {
    const result = validator(value);
    const error = validationResultRoValidationError(
      result,
      definition.validatorErrorTemplate,
      { value, definition, params: definition.parameters }
    );
    if (error !== null) {
      if (definition.name || typeof error === 'string') {
        return { [definition.name || 'error']: error };
      }
      return error;
    }
    return null;
  };
}

export function groupByControlPath<D extends { controlPaths: string[] }>(
  validators: D[]
): { [key: string]: D[] } {
  return validators.reduce(
    (result, validator) => ({
      ...result,
      ...validator.controlPaths.reduce(
        (inner, controlPath) => ({
          ...inner,
          [controlPath]: [...(result[controlPath] || []), validator]
        }),
        {}
      )
    }),
    {}
  );
}

